import { actionColorMap, actionIconMap } from '@/constants/log-action'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useUI } from '@/hooks/useUI'
import type { LogEntry } from '@/types/log'
import { parseLogNote } from '@/utils/log'
import { Clock, History } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ImageUpload } from './ImageUpload'
import { Button } from './ui/button'

const getLogDate = (log: LogEntry) => log.dateCreated ?? log.timestamp ?? log.createdAt ?? ''

export function LogTimeline({
  entries,
  maxItems,
  isLoading = false,
  hiddenActions = [],
  onViewTransferRequest,
}: {
  entries: LogEntry[]
  maxItems?: number
  isLoading?: boolean
  hiddenActions?: string[]
  onViewTransferRequest?: (tranferId: string) => void
}) {
  const { t } = useTranslation()
  const { logActions, deliveryMethods, getEnumLabel } = useUI()
  const { formatDateTime } = useDateFormatter()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
        <Clock className="size-4 animate-spin" />
      </div>
    )
  }

  const hiddenActionSet = new Set(hiddenActions)
  const sorted = entries
    .filter(log => !hiddenActionSet.has(log.action))
    .sort((a, b) => getLogDate(b).localeCompare(getLogDate(a)))
  const display = maxItems ? sorted.slice(0, maxItems) : sorted

  if (display.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
        {t('common.logTimeline.noHistory')}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0" data-testid="log-timeline">
      {display.map((log, i) => {
        const Icon = actionIconMap[log.action] || Clock
        const colorClass = actionColorMap[log.action] || 'text-muted-foreground'
        const isLast = i === display.length - 1
        const timestamp = getLogDate(log)
        const createdByName = log.createdByName ?? log.userName
        const fromDepartmentName = log.fromDepartmentName ?? log.fromDeptName
        const toDepartmentName = log.toDepartmentName ?? log.toDeptName
        const imageIds = log.imageIds ?? []
        const images = log.images ?? []
        const notes = parseLogNote(log.note)
        return (
          <div key={log.id} className="flex gap-3" data-testid={`log-entry-${log.id}`}>
            <div className="flex flex-col items-center">
              <div
                className={`flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary ${colorClass}`}
              >
                <Icon className="size-3.5" />
              </div>
              {!isLast && <div className="w-px flex-1 bg-border/50" />}
            </div>
            <div className={`flex min-w-0 flex-1 flex-col gap-1 ${isLast ? 'pb-0' : 'pb-4'}`}>
              <div className={`flex justify-between gap-1 ${isLast ? 'pb-0' : 'pb-4'}`}>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${colorClass}`}>
                      {getEnumLabel(logActions, log.action, log.action)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {formatDateTime(timestamp, {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  {notes.map((part, index) => (
                    <p key={index} className="text-xs text-muted-foreground">
                      {part.type === 'method'
                        ? `${t('requests.method')}: ${getEnumLabel(deliveryMethods, part.value, part.value)}`
                        : part.value}
                    </p>
                  ))}
                  <div className="flex flex-wrap items-center gap-2">
                    {createdByName && (
                      <span className="text-[10px] text-muted-foreground/70">
                        {t('common.logTimeline.by', { name: createdByName })}
                      </span>
                    )}
                    {fromDepartmentName && toDepartmentName && (
                      <span className="text-[10px] text-muted-foreground/70">
                        {fromDepartmentName} -&gt; {toDepartmentName}
                      </span>
                    )}
                    {log.patientName && (
                      <span className="text-[10px] text-muted-foreground/70">
                        {t('common.logTimeline.patient', { name: log.patientName })}
                      </span>
                    )}
                  </div>
                </div>
                {onViewTransferRequest && log.relatedTransferId && (
                  <div className="flex items-center justify-end">
                    <Button
                      data-testid={`log-entry-history-${log.relatedTransferId}`}
                      className="size-8 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewTransferRequest(log.relatedTransferId ?? '')}
                    >
                      <History />
                    </Button>
                  </div>
                )}
              </div>
              {imageIds.length > 0 && (
                <div className="mt-1">
                  <div className="rounded-lg border border-border/30 p-3">
                    <p className="text-[10px] text-muted-foreground">{t('common.images')}:</p>
                    <div className={'mt-2'}>
                      <ImageUpload
                        files={[]}
                        imageIds={imageIds}
                        onChange={() => {}}
                        maxImages={imageIds.length}
                        readonly
                      />
                    </div>
                  </div>
                </div>
              )}
              {images.length > 0 && (
                <div className="mt-1 flex gap-1">
                  {images.map((img, j) => (
                    <img
                      key={`${log.id}-${j}`}
                      src={img}
                      alt=""
                      className="size-10 rounded border border-border object-cover"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
